/**
 * The words a person searched before they landed on a light's page, read
 * back as the first line of their telling (2026-10-04 拍板, Google only).
 *
 * The Google ads land on a light's page with `utm_source=google` and
 * `utm_term={keyword}` — the keyword the search matched, which on this
 * account is a first-person sentence ("i'm so lonely", "lonely and need
 * someone to talk to"). Four nights of the probe (9/30–10/3): 22 people
 * read the first screen — the title, the count, the question box — and 14
 * left without touching anything; the two who went deepest had first said
 * something to the question box and then pressed the door. The door's
 * heading and its empty field were the homepage's, speaking to nobody in
 * particular. Here the sentence they already typed into Google becomes the
 * door's heading and sits in the field, theirs to finish.
 *
 * Only Google, only a term that reads as a sentence: an arrival from
 * anywhere else, a template that was never filled (`{keyword}`), a term
 * with anything but letters, digits and plain punctuation, or one longer
 * than a line, gets '' and the page as it was. Pure, so the shaping has
 * its own test.
 */

/** Longer than this is not a sentence a person searched. */
const MAX_CHARS = 80;
/** Letters, digits, apostrophes and the plainest punctuation — nothing a URL or a template would bring. */
const ALLOWED = /^[a-z0-9' ,.!?-]+$/i;

/**
 * @param search  window.location.search (with or without the leading ?)
 * @returns the searched words as a sentence — first letter up, the pronoun
 *          `i` up ("i'm so lonely" → "I'm so lonely") — or '' when there is
 *          none to read back.
 */
export function searchedOpening(search: string): string {
  let params: URLSearchParams;
  try { params = new URLSearchParams(search || ''); } catch { return ''; }
  if ((params.get('utm_source') || '').trim().toLowerCase() !== 'google') return '';
  const raw = (params.get('utm_term') || '').replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!raw || raw.length > MAX_CHARS || !ALLOWED.test(raw)) return '';
  const sentence = raw.toLowerCase().replace(/\bi\b/g, 'I');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/** The sentence as the heading quotes it: typographic apostrophe, a full stop when it has no end. */
export function quotedOpening(sentence: string): string {
  const said = sentence.replace(/'/g, '’');
  return /[.!?]$/.test(said) ? said : `${said}.`;
}
